// ============================================================================
// 🚨 IMPORTANT: 3D TILE CACHING CONFIGURATION
// ============================================================================
//
// 3D tile caching is DISABLED by default due to potential Google ToS concerns.
// 
// To enable/disable:
// 1. Find `CacheConfig.tileCachingEnabled` below (around line 50)
// 2. Set to `true` to enable, `false` to disable
// 3. When enabled, review Google's Terms of Service for compliance
//
// ============================================================================

import config from './config';
import axios from 'axios';
import logger from './utils/logger';
import { metresBetween } from './utils/getDistance';
import { PubObject } from "./components/FindPubs";
import { supabase } from './utils/supabaseClient';

// Define response interface for run_garden API
export interface RaycastingResponse {
    success: boolean;
    message: string;
    session: string;
    sun_times: {
        sunrise: string;
        sunset: string;
    };
    heatmap_1d: {
        [google_place_id: string]: {
            [timestamp: string]: number;
        }
    };
    has_no_garden?: boolean;  // Flag indicating if the pub has been marked as having no garden
    garden_details?: {
        [garden_uid: string]: {
            name: string;
            is_uncertain: boolean;
        }
    };
    cached_pubs: string[];
    beer_mug_ratings?: {  // Now optional since we're calculating this in the frontend
        [google_place_id: string]: number; // Rating from 1-5
    };
    total_sun_points: {
        [google_place_id: string]: number; // Raw point-hours of sunlight
    };
    raycast_results?: any;
    garden_coords?: any;
    OFFSET_VAL?: any;
    above_horizon_steps?: number;
    meshes?: any;
    average_sun?: number[];
    warnings?: string;
    copyrights?: string[];  // Array of copyright attributions in descending order of occurrences
    garden_polygons?: {
        [garden_uid: string]: {
            polygon: [number, number][];  // Array of [lat, lon] coordinates
            elevation: number | null;  // Elevation in meters, can be null
        }
    };
    coverage_quality?: 'HIGH' | 'MEDIUM' | 'LOW' | null;  // Solar API data quality level
}

// Interface for Garden data used by API functions
// This is a more strictly typed version of the Garden interface from types/gardenTypes
export interface Garden {
    uid: string;
    name?: string;
    garden_latlon: [number, number][] | [number, number, number][];
    elevation?: number;
    is_uncertain?: boolean;
}

// Interface for Garden sub area
export interface GardenSubArea {
    uid: string;
    sub_area_name: string;
    garden_latlon: [number, number][];
    name?: string;
    polygon?: [number, number][];
    is_uncertain?: boolean;
    elevation?: number;
    garden_size?: number;  // In sqm
}

// Cache configuration object that can be modified at runtime
export const CacheConfig = {
    // Flag to enable/disable caching globally
    // CHANGE THIS TO TRUE TO TURN ON CACHING OF RACYASTING RESULT
    enabled: true,
    
    // Flag specifically for the getPubRaycasting function
    pubRaycastingEnabled: true,
    
    // Flag specifically for the getLatLon function - disable by default
    getLatLonEnabled: false,
    
    // Flag specifically for 3D tile caching
    // ⚠️  DISABLED BY DEFAULT - May violate Google ToS ⚠️
    // 
    // To enable 3D tile caching:
    // 1. Set tileCachingEnabled: true
    // 2. Ensure you have proper licensing from Google
    // 3. Review Google's 3D Tiles API terms of service
    //
    // When enabled, provides:
    // - 45-minute cache expiry (shorter than session expiry)
    // - Session-based cleanup
    // - IndexedDB storage for binary data
    // - Automatic expired tile cleanup
    tileCachingEnabled: false,
};

const api = axios.create({
    baseURL: config.apiBaseUrl,
    timeout: 100000,
    headers: {
        'Content-Type': 'application/json',
    }
});

const urls = {
    run_raycasting: 'run_garden/',
    nearbyPubs: 'search_area/',
    getPubGardens: 'get_pub_sub_area_polygons/',
    // getAuth: 'api-token-auth/',
    // refreshToken: 'api-token-refresh/',
    getPubRaycasting: 'get_pub_raycasting/',
    getUnmoderatedPubs: 'get_unmoderated_pubs/',
    approveGarden: 'approve_garden/',
    // getAppToken: 'get_app_token/', // New secure endpoint
    deleteGarden: 'delete_garden/', // Added endpoint for garden deletion
    pubByGooglePlaceId: 'pub_by_google_place_id/', // Added endpoint to search by Google Place ID
    getCoverageStats: 'get_coverage_stats/', // Added endpoint for coverage statistics
    getSunnyPubsByCity: 'get_sunny_pubs_by_city/',
    getPlaceDetails: 'get_place_details/', // Added endpoint for getting place details directly
    updateUserProfile: 'update_user_profile/', // Added endpoint for updating user profile
    checkSolarCoverage: 'check_solar_coverage/' // Added endpoint for checking Solar API coverage
}

/**
 * Invalidates the raycasting cache for a specific pub.
 * This should be called after any action that modifies a pub's gardens.
 * @param googlePlaceId The Google Place ID of the pub whose cache should be cleared.
 */
export const invalidatePubRaycastingCache = (googlePlaceId: string) => {
    if (!googlePlaceId) return;

    const timezone = getStoredTimezone();
    // Clear both detail and simple view caches for the pub
    const detailCacheKey = `pub_raycasting_${googlePlaceId}_detail_${timezone}`;
    const simpleCacheKey = `pub_raycasting_${googlePlaceId}_simple_${timezone}`;

    clientCache.remove(detailCacheKey);
    clientCache.remove(simpleCacheKey);

    logger.info(`Raycasting cache invalidated for pub: ${googlePlaceId}`);
};

export const getPubRaycasting = async (
    googlePlaceId: string,
    detailView: boolean = false,
    skipCache: boolean = false
) => {

    const timezone = getStoredTimezone();
    const params = { 
        'google_place_id': googlePlaceId,
        'detail_view': detailView.toString(),
        'timezone': timezone
    };

    const cacheKey = `pub_raycasting_${googlePlaceId}_${detailView ? 'detail' : 'simple'}_${timezone}`;
    const useCaching = CacheConfig.enabled && CacheConfig.pubRaycastingEnabled && !skipCache;
    
    const cachedResult = useCaching ? clientCache.get(cacheKey) : null;
    if (cachedResult) {
        if (!('has_no_garden' in cachedResult)) {
            cachedResult.has_no_garden = false;
        }
        if (!cachedResult.garden_details && cachedResult.garden_names) {
            cachedResult.garden_details = {};
            for (const gardenId in cachedResult.garden_names) {
                cachedResult.garden_details[gardenId] = {
                    name: cachedResult.garden_names[gardenId],
                    is_uncertain: false
                };
            }
        }
        if (!cachedResult.copyrights) {
            cachedResult.copyrights = [];
        }
        return { data: cachedResult };
    }
    
    const requestKey = `request_${cacheKey}`;
    const pendingRequest = getPendingRequest(requestKey);
    
    if (pendingRequest) {
        return pendingRequest;
    }
    
    try {
        const request = api.get(urls.getPubRaycasting, { params });
        storePendingRequest(requestKey, request);
        
        const response = await request;
        
        if (response.data && !response.data.copyrights) {
            response.data.copyrights = [];
        }
        
        if (response.data && useCaching) {
            clientCache.set(cacheKey, response.data);
        }
        
        clearPendingRequest(requestKey);
        
        return response;
    } catch (error) {
        clearPendingRequest(requestKey);
        logger.error(`Error fetching raycasting data for pub ${googlePlaceId}:`, error);
        throw error;
    }
}

// Request deduplication system
const pendingRequests: Record<string, Promise<any>> = {};

function getPendingRequest(key: string): Promise<any> | null {
    return pendingRequests[key] || null;
}

function storePendingRequest(key: string, request: Promise<any>): void {
    pendingRequests[key] = request;
    
    // Auto-cleanup after 30 seconds in case of hanging requests
    setTimeout(() => {
        clearPendingRequest(key);
    }, 30000);
}

function clearPendingRequest(key: string): void {
    delete pendingRequests[key];
}

// Simple client cache implementation
const clientCache = {
    // Cache lifetime in milliseconds (3 hours)
    CACHE_EXPIRY: 3 * 60 * 60 * 1000,
    
    // Get item from cache
    get: (key: string) => {
        try {
            const cachedData = localStorage.getItem(`cache_${key}`);
            if (!cachedData) return null;
            
            const { timestamp, data } = JSON.parse(cachedData);
            const now = new Date().getTime();
            
            // Check if cache is expired
            if (now - timestamp > clientCache.CACHE_EXPIRY) {
                localStorage.removeItem(`cache_${key}`);
                return null;
            }
            
            return data;
        } catch (e) {
            logger.warn('Cache get failed:', e);
            return null;
        }
    },
    
    // Store item in cache
    set: (key: string, data: any) => {
        try {
            const cacheObject = {
                timestamp: new Date().getTime(),
                data: data
            };
            localStorage.setItem(`cache_${key}`, JSON.stringify(cacheObject));
        } catch (e) {
            logger.warn('Cache set failed:', e);
        }
    },
    
    // Remove item from cache
    remove: (key: string) => {
        try {
            localStorage.removeItem(`cache_${key}`);
        } catch (e) {
            logger.warn('Cache remove failed:', e);
        }
    },
    
    // Create a consistent cache key for raycasting
    createRaycastingKey: (pubs: string[], startTime: string, endTime: string) => {
        // Sort pubs for consistent key generation regardless of order
        return `raycasting_${[...pubs].sort().join('_')}_${startTime}_${endTime}`;
    }
};

// Safe storage wrapper
const storage = {
    get: (key: string) => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                return localStorage.getItem(key);
            }
            return null;
        } catch (e) {
            logger.warn('Storage get failed:', e);
            return null;
        }
    },

    set: (key: string, value: string) => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.setItem(key, value);
                return true;
            }
            return false;
        } catch (e) {
            logger.warn('Storage set failed:', e);
            return false;
        }
    }
};


// Add token to all requests - tries Supabase first, then anonymous JWT
api.interceptors.request.use(
  async (config) => {
    // Skip token handling for any auth-related endpoints IF you had specific ones.
    // For Supabase, this is generally not an issue as auth is handled by supabase client.
    
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Error getting Supabase session for API request:', sessionError.message);
        // Proceed without Authorization header if session retrieval fails
        return config;
      }

      if (sessionData?.session?.access_token) {
        config.headers.Authorization = `Bearer ${sessionData.session.access_token}`;
        // console.log('Attaching Supabase token to API request:', sessionData.session.access_token.substring(0,15) + "...");
      } else {
        // This case should be rare if AuthProvider ensures an anonymous session
        console.warn('No Supabase session token available for API request. Request will be unauthenticated.');
      }
    } catch (e) {
      console.error('Unexpected error in API interceptor while getting Supabase session:', e);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

export const runRaycasting = async (
    pubs: string[],
    gardenLatLon: [string, string][][],
    startTime: string,
    endTime: string,
    rel_err_threshold: number = 13,
    show_heatmap: boolean = false,
    list_view: boolean = false,
    lowResolution: boolean = false,
): Promise<{ data: RaycastingResponse }> => {
    // Create a unique cache key for this request - using the helper method for consistency
    const cacheKey = clientCache.createRaycastingKey(pubs, startTime, endTime);
    
    // Check if we have this result in cache
    const cachedResult = clientCache.get(cacheKey);
    if (cachedResult) {
        //logger.info('Using cached raycasting result');
        
        // When returning from cache, ensure we have beer mug ratings
        // This handles cases where the cached data might be missing ratings
        if (!cachedResult.beer_mug_ratings && cachedResult.total_sun_points) {
            //logger.info('Recalculating beer mug ratings for cached result');
            
            // Create beer mug ratings if they don't exist
            cachedResult.beer_mug_ratings = {};
            
            // Calculate ratings based on total_sun_points
            for (const googlePlaceId in cachedResult.total_sun_points) {
                const pointHours = cachedResult.total_sun_points[googlePlaceId];
                // Calculate beer mug rating (similar logic as in calculateBeerMugRating)
                let rating = 1;  // Default
                
                // Simple rating calculation based on point-hours
                if (pointHours >= 5000) rating = 5;
                else if (pointHours >= 3000) rating = 4;
                else if (pointHours >= 1000) rating = 3;
                else if (pointHours >= 500) rating = 2;
                else if (pointHours >= 10) rating = 1;
                else rating = 1;
                
                cachedResult.beer_mug_ratings[googlePlaceId] = rating;
            }
            
            // Update the cache with the enhanced data
            clientCache.set(cacheKey, cachedResult);
        }
        
        // Return in the same structure as an axios response for consistency
        return { data: cachedResult };
    }

    // In case of cache miss, create a promise to track this request
    // This prevents duplicate requests for the same data during race conditions
    try {
        const data = {
            names: pubs,
            garden_latlons: gardenLatLon,
            start_time: startTime,
            end_time: endTime,
            verbose: !list_view,
            max_geometric_error: 85,
            show_lod_map: false,
            min_resolution: 15,
            rel_err_threshold: rel_err_threshold,
            garden_vertex_array_spacing: 0.5,
            show_heatmap: show_heatmap,
            cache_override: false,
            compress_response: false,
            comms_compression_threshold: 1,
            concurrent_limit: 25,
            list_view: list_view
        }

        const response = await api.post(urls.run_raycasting, data);
        
        if (response.data && response.data.success) {
            // Ensure beer mug ratings exist before caching
            if (!response.data.beer_mug_ratings && response.data.total_sun_points) {
                logger.info('Adding beer mug ratings to API response before caching');
                
                // Create beer mug ratings object
                response.data.beer_mug_ratings = {};
                
                // Calculate ratings based on total_sun_points
                for (const googlePlaceId in response.data.total_sun_points) {
                    const pointHours = response.data.total_sun_points[googlePlaceId];
                    // Calculate beer mug rating
                    let rating = 1;  // Default
                    
                    // Simple rating calculation based on point-hours
                    if (pointHours >= 5000) rating = 5;
                    else if (pointHours >= 3000) rating = 4;
                    else if (pointHours >= 1000) rating = 3;
                    else if (pointHours >= 500) rating = 2;
                    else if (pointHours >= 10) rating = 1;
                    else rating = 1;
                    
                    response.data.beer_mug_ratings[googlePlaceId] = rating;
                }
            }
            
            // Only cache successful responses
            clientCache.set(cacheKey, response.data);
        }
        
        return response;
    } catch (error) {
        logger.error('Raycasting API error:', error);
        throw error;
    }
}

// Get all SubAreas for the given Pub
export const getPubGardens = async (
    googlePlaceId: string
) => {
    if (!googlePlaceId) {
        logger.warn('Attempted to get pub data with undefined google_place_id');
        return Promise.reject(new Error('Invalid pub ID'));
    }
    
    // Create a cache key for this pub's data
    const cacheKey = `pub_polygon_${googlePlaceId}`;
    
    // Check if caching is enabled globally AND specifically for this function
    const useCaching = CacheConfig.enabled && CacheConfig.getLatLonEnabled;
    
    // Check cache first (only if caching is enabled)
    if (useCaching) {
        const cachedResult = clientCache.get(cacheKey);
        if (cachedResult) {
            //logger.info('Using cached pub polygon data');
            return { data: cachedResult };
        }
    }

    // Add request deduplication to prevent duplicate calls
    const requestKey = `request_polygon_${googlePlaceId}`;
    const pendingRequest = getPendingRequest(requestKey);
    
    if (pendingRequest) {
        //logger.info(`Reusing existing polygon request for pub ${google_place_id}`);
        return pendingRequest;
    }

    try {
        const data = {'google_place_id': googlePlaceId};
        // Store this request in pending requests
        const request = api.get(urls.getPubGardens, {params: data});
        storePendingRequest(requestKey, request);
        
        const response = await request;
        
        // Cache the successful response (only if caching is enabled)
        if (response.data && useCaching) {
            clientCache.set(cacheKey, response.data);
        }
        
        // Remove from pending requests
        clearPendingRequest(requestKey);
        
        return response;
    } catch (error) {
        // Remove from pending requests on error
        clearPendingRequest(requestKey);
        
        logger.error('GetLatLon API error:', error);
        throw error;
    }
}

// Add new searchCities function for city search
export const searchCities = async (query: string, limit: number = 10) => {
    if (!query || query.length < 2) {
        // Return empty result for short queries
        return { data: { cities: [] } };
    }
    
    try {
        // Create base URL that handles trailing slashes
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        
        const response = await api.get(`${baseUrl}search_cities/`, {
            params: { query, limit }
        });
        
        return response;
    } catch (error) {
        logger.error('Error searching cities:', error);
        throw error;
    }
};

export const getNearbyPubs = async (
    lat: number,
    lon: number,
    radius: number = 1000,
    country = "GB",
    ormLookup: boolean = true, // Retained for ViewPub's cheap search
    desiredPubCount: number = 10 // How many closest pubs we want as a minimum
) => {
    const data = {
        lat: lat,
        lon: lon,
        radius: radius,
        country: country,
        orm_lookup: ormLookup,
        desired_pub_count: desiredPubCount
    };

    // Cache key now includes all relevant parameters
    const cacheKey = `nearby_pubs_${lat.toFixed(4)}_${lon.toFixed(4)}_${radius}_${ormLookup}_${desiredPubCount}`;
    
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
        try {
            const cache = JSON.parse(cachedData);
            const timestamp = cache.timestamp;
            const now = Date.now();
            const cacheAge = now - timestamp;
            const maxCacheAge = 60 * 60 * 1000; // 1 hour
            
            if (cacheAge < maxCacheAge) {
                return { data: cache.response };
            }
        } catch (error) {
            console.error('Error parsing nearby pubs cache:', error);
        }
    }

    const requestKey = `request_nearby_pubs_${lat.toFixed(4)}_${lon.toFixed(4)}_${radius}_${ormLookup}_${desiredPubCount}`;
    const pendingRequest = getPendingRequest(requestKey);
    if (pendingRequest) {
        return pendingRequest;
    }

    try {
        const request = api.get(urls.nearbyPubs, {params: data});
        storePendingRequest(requestKey, request);
        const response = await request;
        
        try {
            const cacheObject = {
                timestamp: Date.now(),
                response: response.data
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheObject));
        } catch (cacheError) {
            console.error('Failed to cache nearby pubs response:', cacheError);
        }
        
        clearPendingRequest(requestKey);
        return response;
    } catch (error) {
        clearPendingRequest(requestKey);
        logger.error(`Error fetching nearby pubs: ${error}`);
        throw error;
    }
}

// Admin functions 
export const getUnmoderatedPubs = async (page: number = 1, pageSize: number = 10) => {
    try {
        const response = await api.get(urls.getUnmoderatedPubs, {
            params: {
                page,
                page_size: pageSize
            }
        });
        return response;
    } catch (error) {
        logger.error('Failed to fetch unmoderated pubs:', error);
        throw error;
    }
};

export const approveGarden = async (googlePlaceId: string) => {
    try {
        const response = await api.post(urls.approveGarden, {
            google_place_id: googlePlaceId
        });
        return response;
    } catch (error) {
        logger.error('Failed to approve garden:', error);
        throw error;
    }
};

/**
 * Get the sunniest pubs for a specific city
 * @param city The city name to search for
 * @param limit Maximum number of pubs to return (default: 10)
 * @param date Optional specific date to use (format: YYYY-MM-DD)
 * @returns List of pubs sorted by sun points
 */
/**
 * Get sunny pubs for enhanced city pages - handles special cases
 */
export const getSunnyPubsForEnhancedCity = async (citySlug: string, limit: number = 15) => {
    let searchTerm = '';
    
    switch(citySlug) {
        case 'london':
            // This will match "London", "Westminster (London)", "Lewisham (London)", etc.
            searchTerm = 'London';
            break;
        case 'bristol':
            searchTerm = 'Bristol';
            break;
        case 'brighton-and-hove':
            searchTerm = 'Brighton';
            break;
        default:
            throw new Error('Invalid city for enhanced page');
    }
    
    return getSunnyPubsByCity(searchTerm, limit);
};

export const getSunnyPubsByCity = async (city: string, limit: number = 10, date?: string) => {
    try {
        const params: Record<string, string | number> = {
            city,
            limit,
            _t: Date.now()
        };
        
        if (date) {
            params.date = date;
        }
        
        const response = await api.get(`${urls.getSunnyPubsByCity}`, { params });
        return response;
    } catch (error) {
        logger.error('Error fetching sunny pubs by city:', error);
        throw error;
    }
};

/**
 * Get pub details and nearby pubs in a single API call. To be used only on ViewPub page.
 * @param googlePlaceId Google Place ID to search for
 * @returns Pub details and nearby pubs if found
 */
export const getPubWithNearby = async (googlePlaceId: string) => {
    if (!googlePlaceId) {
        throw new Error('Google Place ID is required');
    }
    
    try {
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        return await api.get(`${baseUrl}get_pub_with_nearby/`, {
            params: { google_place_id: googlePlaceId }
        });
    } catch (error) {
        logger.error('Error fetching pub with nearby:', error);
        throw error;
    }
};

/**
 * Search for a pub by Google Place ID
 * @param googlePlaceId Google Place ID to search for
 * @returns Pub details if found, or null if not found
 */
export const getPubByGooglePlaceId = async (googlePlaceId: string) => {
    if (!googlePlaceId) {
        throw new Error('Google Place ID is required');
    }
    
    try {
        const response = await api.get(urls.pubByGooglePlaceId, {
            params: { google_place_id: googlePlaceId },
        });
        return response;
    } catch (error: any) {
        // If the error is 404, it means the pub doesn't exist, which is an expected case
        if (error.response && error.response.status === 404) {
            return { data: { exists: false } };
        }
        logger.error('Error fetching pub by Google Place ID:', error);
        throw error;
    }
};

/**
 * Create a new pub
 * @param pubData Object containing pub details (name, address, city, coordinates, etc.)
 * @returns API response with the created pub information
 */
export const createPub = async (pubData: any) => {
    if (!pubData || !pubData.pub) {
        throw new Error('Invalid pub data');
    }
    
    try {
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        return await api.post(`${baseUrl}create_pub/`, pubData);
    } catch (error) {
        logger.error('Error creating pub:', error);
        throw error;
    }
};

/**
 * Delete a garden by UID
 * @param gardenUid The UID of the garden to delete
 */
export const deleteGarden = async (gardenUid: string) => {
    if (!gardenUid) {
        throw new Error('Garden UID is required');
    }
    
    try {
        const baseUrl = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl : `${config.apiBaseUrl}/`;
        return await api.delete(`${baseUrl}${urls.deleteGarden}`, {
            data: { uid: gardenUid }
        });
    } catch (error) {
        logger.error('Error deleting garden:', error);
        throw error;
    }
};

/**
 * Toggle the uncertainty status of a garden
 * @param googlePlaceId The ID of the pub that owns the garden
 * @param garden The garden to update
 * @param isUncertain Whether the garden should be marked as uncertain
 */
export const updateGardenUncertainty = async (
    googlePlaceId: string, 
    garden: Garden | { uid: string; name?: string; garden_latlon: any[]; elevation?: number; is_uncertain?: boolean },
    isUncertain: boolean
) => {
    if (!googlePlaceId || !garden || !garden.uid) {
        throw new Error('Invalid pub ID or garden data');
    }
    
    try {
        // Prepare polygon data using existing garden data, only changing 'is_uncertain'
        // Handle different garden_latlon formats safely
        const polygonData = garden.garden_latlon.map((coord: any) => {
            // Extract first two elements regardless of array format
            return [
                Array.isArray(coord) ? coord[0] : coord.lat || coord.latitude,
                Array.isArray(coord) ? coord[1] : coord.lon || coord.longitude
            ];
        });
        
        const gardenElevation = garden.elevation || 0;
        
        const payload = {
            pub: {
                google_place_id: googlePlaceId,
                garden_drawn: true,
            },
            sub_areas: [
                {
                    uid: garden.uid,
                    name: garden.name || 'Garden',
                    polygon: polygonData,
                    elevation: gardenElevation,
                    is_uncertain: isUncertain,
                }
            ]
        };
        
        return await api.put(`${config.apiBaseUrl}pubs/${googlePlaceId}/`, payload);
    } catch (error) {
        logger.error('Error updating garden uncertainty:', error);
        throw error;
    }
};



/**
 * Get the full meshes for a pub without any sunlight calculation.
 * Used for displaying the 3D map for garden submission.
 * @param googlePlaceId The Google Place ID of the pub.
 * @param latitude Optional latitude. If provided with longitude, these are used instead of a DB lookup.
 * @param longitude Optional longitude. If provided with latitude, these are used instead of a DB lookup.
 * @returns API response containing mesh URIs, session ID, and other necessary data.
 */
export const getFullMeshes = async (googlePlaceId: string, latitude?: number, longitude?: number) => {
    if (!googlePlaceId) {
        throw new Error('Google Place ID is required');
    }

    const cacheKey = `full_meshes_${googlePlaceId}`;
    // Check if we have this result in cache
    const cachedResult = clientCache.get(cacheKey);
    if (cachedResult && CacheConfig.enabled) {
        logger.info(`Using cached full meshes for pub ${googlePlaceId}`);
        return { data: cachedResult };
    }

    try {
        const params: { google_place_id: string, lat?: number, lon?: number } = {
            google_place_id: googlePlaceId
        };
        // If lat/lon are provided and are not 0,0, pass them.
        // This gives the backend fresh coordinates if its DB is out of sync.
        if (latitude !== undefined && longitude !== undefined && (latitude !== 0 || longitude !== 0)) {
            params.lat = latitude;
            params.lon = longitude;
        }

        const response = await api.get('get_full_meshes/', {
            params: params
        });

        if (response.data && CacheConfig.enabled) {
            clientCache.set(cacheKey, response.data);
            logger.info(`Cached full meshes for pub ${googlePlaceId}`);
        }
        return response;
    } catch (error) {
        logger.error('Error fetching full meshes:', error);
        throw error;
    }
};

/**
 * Mark a pub as having no garden
 * @param googlePlaceId The ID of the pub to mark
 */
export const markPubHasNoGarden = async (googlePlaceId: string) => {
    if (!googlePlaceId) {
        throw new Error('Pub ID is required');
    }
    
    try {
        const payload = {
            pub: {
                has_no_garden: true
            },
            sub_areas: [] // Send empty array to potentially clear existing gardens
        };
        
        return await api.put(`${config.apiBaseUrl}pubs/${googlePlaceId}/`, payload);
    } catch (error) {
        logger.error('Error marking pub as having no garden:', error);
        throw error;
    }
};

/**
 * Store the submitter's name in localStorage for future use
 * @param name The name of the person submitting the garden
 */
export const storeSubmitterName = (name: string): void => {
    try {
        localStorage.setItem('garden_submitter_name', name);
    } catch (error) {
        console.error('Error storing submitter name:', error);
    }
};

/**
 * Retrieve the submitter's name from localStorage
 * @returns The stored name or empty string if not found
 */
export const getSubmitterName = (): string => {
    try {
        return localStorage.getItem('garden_submitter_name') || '';
    } catch (error) {
        console.error('Error retrieving submitter name:', error);
        return '';
    }
};

/**
 * Submit garden data to the database
 * @param googlePlaceId The ID of the pub
 * @param convertedCoordinates The garden coordinates in [lat, lon, elev] format
 * @param gardenName The name of the garden
 * @param avgElevation The average elevation of the garden
 * @param isUncertain Whether the garden is uncertain
 * @param existingGardenUid The UID of an existing garden to update (optional)
 * @param submitterName The name of the person who submitted the garden
 */
export const submitGardenToDatabase = async (
    googlePlaceId: string,
    convertedCoordinates: [number, number, number][],
    gardenName: string,
    avgElevation: number,
    isUncertain: boolean = false,
    existingGardenUid?: string,
    submitterName?: string
) => {
    if (!googlePlaceId || !convertedCoordinates || convertedCoordinates.length < 3) {
        throw new Error('Invalid pub ID or garden coordinates');
    }
    
    try {
        // Prepare polygon data (Lat, Lon pairs)
        const polygonData = convertedCoordinates.map(coord => [coord[0], coord[1]]);
        
        // Construct payload
        const payload = {
            pub: {
                google_place_id: googlePlaceId,
                garden_drawn: true,
                has_no_garden: false,
            },
            sub_areas: [
                {
                    name: gardenName,
                    polygon: polygonData,
                    is_uncertain: isUncertain,
                    elevation: avgElevation,
                    submitter_name: submitterName,
                    ...(existingGardenUid && { uid: existingGardenUid })
                }
            ]
        };
        
        // Store the submitter name for future use if provided
        if (submitterName) {
            storeSubmitterName(submitterName);
        }
        
        return await api.put(`${config.apiBaseUrl}pubs/${googlePlaceId}/`, payload);
    } catch (error: any) {
        // Check for permission denied errors
        if (error.response?.status === 403 && error.response?.data?.error_type === 'permission_denied') {
            throw {
                type: 'permission_denied',
                message: error.response.data.error
            };
        }
        logger.error('Error submitting garden data:', error);
        throw error;
    }
};

// Coordinate conversion utilities moved from SubmitGarden3D

/**
 * Converts latitude, longitude, and elevation to ECEF coordinates
 * @param lat Latitude in degrees
 * @param lon Longitude in degrees
 * @param elevation Elevation in meters
 * @returns ECEF coordinates [x, y, z]
 */
export const latLonElevToECEF = (lat: number, lon: number, elevation: number = 0): [number, number, number] => {
    const latRad = (lat * Math.PI) / 180;
    const lonRad = (lon * Math.PI) / 180;
    const a = 6378137.0;
    const f = 1/298.257223563;
    const e2 = 2*f - f*f;
    const N = a / Math.sqrt(1 - e2 * Math.sin(latRad) * Math.sin(latRad));
    const X = (N + elevation) * Math.cos(latRad) * Math.cos(lonRad);
    const Y = (N + elevation) * Math.cos(latRad) * Math.sin(lonRad);
    const Z = (N * (1 - e2) + elevation) * Math.sin(latRad);
    return [X, Y, Z];
};

/**
 * Converts ECEF coordinates to latitude, longitude, and elevation
 * @param x X coordinate in ECEF
 * @param y Y coordinate in ECEF
 * @param z Z coordinate in ECEF
 * @returns [latitude, longitude, elevation] in degrees and meters
 */
export const ECEFToLatLonElev = (x: number, y: number, z: number): [number, number, number] => {
    const a = 6378137.0;
    const f = 1/298.257223563;
    const b = a * (1 - f);
    const e2 = 2*f - f*f;
    const lon = Math.atan2(y, x);
    const p = Math.sqrt(x*x + y*y);
    if (p < 1e-10) {
        const sign = Math.sign(z);
        return [sign * 90, 0, Math.abs(z) - b];
    }
    let lat = Math.atan2(z, p * (1 - e2));
    let N, h, prev;
    for (let i = 0; i < 5; i++) {
        prev = lat;
        const sinLat = Math.sin(lat);
        N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
        h = p / Math.cos(lat) - N;
        lat = Math.atan2(z, p * (1 - e2 * N / (N + h)));
        if (Math.abs(lat - prev) < 1e-10) break;
    }
    const sinLat = Math.sin(lat);
    N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
    h = p / Math.cos(lat) - N;
    const latDeg = (lat * 180) / Math.PI;
    const lonDeg = (lon * 180) / Math.PI;
    return [latDeg, lonDeg, h];
};

/**
 * Wrapper for ECEFToLatLonElev for compatibility
 */
export const gardenCallbackECEFToLatLonElev = (x_api: number, y_api: number, z_api: number): [number, number, number] => {
    return ECEFToLatLonElev(x_api, y_api, z_api);
};

/**
 * Check if an operation is just changing elevation
 * @param newCoordinatesECEF New ECEF coordinates
 * @param existingGarden Existing garden data
 * @returns True if only elevation has changed, false otherwise
 */
export const isElevationChangeOperation = (
    newCoordinatesECEF: [number, number, number][],
    existingGarden: GardenSubArea
): boolean => {
    // If the garden lacks coordinates or shapes are different, it's not just an elevation change
    if (!existingGarden.garden_latlon || existingGarden.garden_latlon.length !== newCoordinatesECEF.length) {
        return false;
    }
    
    try {
        const existingPolygonLatLon = existingGarden.garden_latlon;
        
        // Compare each point, ignoring elevation
        for (let i = 0; i < newCoordinatesECEF.length; i++) {
            const newPointLatLon = ECEFToLatLonElev(
                newCoordinatesECEF[i][0],
                newCoordinatesECEF[i][1],
                newCoordinatesECEF[i][2]
            );
            
            // Compare latitude and longitude with a small tolerance
            const latDiff = Math.abs(newPointLatLon[0] - existingPolygonLatLon[i][0]);
            const lonDiff = Math.abs(newPointLatLon[1] - existingPolygonLatLon[i][1]);
            
            // Define a suitable tolerance (approximately 1 meter)
            const tolerance = 0.00001;
            
            if (latDiff > tolerance || lonDiff > tolerance) {
                return false; // Points differ significantly in location
            }
        }
        
        // If all points match geographically, it's likely an elevation change
        return true;
    } catch (err) {
        console.error("Error comparing coordinates for elevation change check:", err);
        return false; // Fail safe
    }
};

/**
 * Get coverage statistics by city
 * @returns API response with city coverage stats
 */
export const getCoverageStatistics = async () => {
    try {
        const response = await api.get(urls.getCoverageStats);
        return response;
    } catch (error) {
        logger.error('Error fetching coverage statistics:', error);
        throw error;
    }
};

export interface PubYearlySunPoints {
    status: string;
google_place_id: string;
    pub_name: string;
    dates: string[];
    sun_percentages: number[];
}

export const getPubYearlySunPoints = async (googlePlaceId: string): Promise<PubYearlySunPoints> => {
    // Create a cache key for this request
    const cacheKey = `yearly_sun_points_${googlePlaceId}`;
    
    // Safety mechanism: Use a try-catch for all cache operations
    let cachedResult = null;
    try {
        // Only attempt to read from cache if caching is enabled globally
        if (CacheConfig.enabled) {
            cachedResult = clientCache.get(cacheKey);
        }
    } catch (error) {
        // Log but don't throw - we'll just proceed with the API call
        console.warn('Failed to read yearly sun data from cache, continuing with API call:', error);
    }
    
    // If we have a valid cached result, return it
    if (cachedResult) {
        return cachedResult;
    }
    
    // Otherwise make the API call
    try {
        // Use the correct endpoint URL from the Django URLconf: get_pub_yearly_sun_points
        const response = await api.get(`get_pub_yearly_sun_points/`, {
            params: { google_place_id: googlePlaceId }
        });
        
        // Attempt to cache the result, but don't block if it fails
        if (CacheConfig.enabled) {
            try {
                clientCache.set(cacheKey, response.data);
            } catch (cacheError) {
                // Just log the error - the user still gets fresh data from the API
                console.warn('Failed to cache yearly sun data, but API call succeeded:', cacheError);
            }
        }
        
        return response.data;
    } catch (error) {
        console.error('Error fetching yearly sun points:', error);
        // Return a valid but empty response structure instead of throwing
        return {
            status: 'error',
            google_place_id: googlePlaceId, // Include the requested ID
            pub_name: '', // Empty string as placeholder
            dates: [],
            sun_percentages: []
        };
    }
};

/**
 * Get pub details directly from Google Places API using place_id
 * This function uses the get_place_details endpoint which directly contacts Google
 * @param googlePlaceId Google Place ID to search for
 * @param minimal If true, only returns essential data (name, place_id) to reduce API costs
 * @returns Pub details if found
 */
export const getPlaceDetails = async (googlePlaceId: string, minimal: boolean = false) => {
    if (!googlePlaceId) {
        throw new Error('Google Place ID is required');
    }
    
    // Create a cache key that includes the minimal flag
    const cacheKey = `place_details_${googlePlaceId}_${minimal ? 'minimal' : 'full'}`;
    
    // Check if this request is already in progress
    const pendingRequest = getPendingRequest(cacheKey);
    if (pendingRequest) {
        return pendingRequest;
    }
    
    try {
        // Create the request with minimal parameter and store it as pending
        const request = api.get(urls.getPlaceDetails, {
            params: { 
                google_place_id: googlePlaceId,
                minimal: minimal.toString()
            }
        });
        
        storePendingRequest(cacheKey, request);
        
        const response = await request;
        clearPendingRequest(cacheKey);
        return response;
    } catch (error: any) {
        clearPendingRequest(cacheKey);
        
        // If the error is 404, it means the pub doesn't exist
        if (error.response && error.response.status === 404) {
            return { data: { error: 'Pub not found' } };
        }
        
        logger.error('Error fetching place details:', error);
        throw error;
    }
};

/**
 * Get weather data for a specific location
 * @param latitude Latitude coordinate
 * @param longitude Longitude coordinate
 * @param includeForecast Whether to include forecast data (default: true)
 * @returns Weather data for the specified location
 */
export const getWeather = async (
  latitude: number,
  longitude: number,
  includeForecast: boolean = true
) => {
  try {
    // Create a cache key based on location and whether forecast is included
    const cacheKey = `weather_${latitude.toFixed(4)}_${longitude.toFixed(4)}_${includeForecast}`;
    
    // Check if we have a cached result with proper expiry logic
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const cache = JSON.parse(cachedData);
        const timestamp = cache.timestamp;
        
        // Check if cache is still valid (less than 90 minutes old)
        const now = Date.now();
        const cacheAge = now - timestamp;
        const maxCacheAge = 90 * 60 * 1000; // 90 minutes
        
        if (cacheAge < maxCacheAge) {
          return { data: cache.response };
        }
      } catch (error) {
        console.error('Error parsing weather cache:', error);
      }
    }
    
    // Add request deduplication
    const requestKey = `request_weather_${latitude}_${longitude}_${includeForecast}`;
    const pendingRequest = getPendingRequest(requestKey);
    
    if (pendingRequest) {
      return pendingRequest;
    }
    
    // Make the API request
    const request = api.get('get_weather/', {
      params: {
        lat: latitude,
        lon: longitude,
        forecast: includeForecast
      }
    });
    
    storePendingRequest(requestKey, request);
    
    const response = await request;
    
    // Cache the response
    try {
      const cacheObject = {
        timestamp: Date.now(),
        response: response.data
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheObject));
    } catch (cacheError) {
      console.error('Failed to cache weather response:', cacheError);
    }
    
    // Clear the pending request
    clearPendingRequest(requestKey);
    
    return response;
  } catch (error) {
    clearPendingRequest(`request_weather_${latitude}_${longitude}_${includeForecast}`);
    logger.error('Error fetching weather data:', error);
    throw error;
  }
};

// TIMEZONE

export const getUserTimezone = (): string => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.error("Error getting timezone:", error);
      return "Europe/London"; // Fallback
    }
  };

// Store the timezone in localStorage for persistence
export const storeUserTimezone = (): void => {
    try {
      const timezone = getUserTimezone();
      localStorage.setItem('user_timezone', timezone);
    } catch (error) {
      console.error("Error storing timezone:", error);
    }
  };

export const getStoredTimezone = (): string => {
    try {
        return localStorage.getItem('user_timezone') || getUserTimezone();
    } catch (error) {
        console.error("Error retrieving timezone:", error);
        return "Europe/London"; // Fallback
    }
};

/**
 * Update user profile information
 * @param displayName User's display name for leaderboards
 * @param showOnLeaderboards Whether to show user on public leaderboards
 * @returns API response with update status
 */
export const updateUserProfile = async (
    displayName: string,
    showOnLeaderboards: boolean
) => {
    try {
        const response = await api.post(urls.updateUserProfile, {
            display_name: displayName,
            show_on_leaderboards: showOnLeaderboards,
        });
        return response;
    } catch (error) {
        logger.error('Error updating user profile:', error);
        throw error;
    }
};

/**
 * Submit feedback for a verified garden.
 * @param googlePlaceId The ID of the pub
 * @param feedbackType The type of feedback ('outline', 'elevation', 'other')
 * @param message The user's message (for 'other' type)
 */
export const submitGardenFeedback = async (
    googlePlaceId: string,
    feedbackType: 'outline' | 'elevation' | 'other',
    message: string
) => {
    try {
        const response = await api.post('submit_feedback/', {
            google_place_id: googlePlaceId,
            feedback_type: feedbackType,
            message: message,
        });
        return response;
    } catch (error) {
        logger.error('Error submitting garden feedback:', error);
        throw error;
    }
};

/**
 * Check if a location is within Google Solar API coverage area.
 * @param latitude Latitude coordinate
 * @param longitude Longitude coordinate
 * @returns Object with coverage status, quality level, and location info
 */
export const checkSolarCoverage = async (
    latitude: number,
    longitude: number
): Promise<{
    status: 'covered' | 'no_coverage' | 'error';
    message: string;
    has_coverage: boolean;
    quality: 'HIGH' | 'MEDIUM' | null;
    location: { lat: number; lon: number };
}> => {
    // Validate input parameters
    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
        throw new Error('Latitude and longitude must be numbers');
    }

    if (latitude < -90 || latitude > 90) {
        throw new Error('Latitude must be between -90 and 90 degrees');
    }

    if (longitude < -180 || longitude > 180) {
        throw new Error('Longitude must be between -180 and 180 degrees');
    }

    // Check for pending requests to avoid duplicates
    const requestKey = `request_solar_coverage_${latitude}_${longitude}`;
    const pendingRequest = getPendingRequest(requestKey);
    
    if (pendingRequest) {
        return pendingRequest;
    }

    try {
        // Make the API request
        const request = api.get(urls.checkSolarCoverage, {
            params: {
                lat: latitude,
                lon: longitude
            }
        });
        
        storePendingRequest(requestKey, request);
        
        const response = await request;
        
        clearPendingRequest(requestKey);
        
        return response.data;
    } catch (error: any) {
        clearPendingRequest(requestKey);
        
        // Handle specific error cases
        if (error.response?.status === 400) {
            throw new Error(`Invalid coordinates: ${error.response.data.error || 'Bad request'}`);
        }
        
        if (error.response?.status === 500) {
            logger.error('Solar coverage check failed on server:', error);
            throw new Error('Server error while checking solar coverage');
        }
        
        logger.error('Error checking solar coverage:', error);
        throw error;
    }
};

// 3D Tile Caching System
class TileCache {
    private dbName = 'suntap_tile_cache';
    private dbVersion = 1;
    private storeName = 'tiles';
    private db: IDBDatabase | null = null;
    
    // Cache for 45 minutes (shorter than session expiry)
    private readonly TILE_CACHE_EXPIRY = 45 * 60 * 1000;
    
    async init(): Promise<void> {
        if (this.db) return;
        
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);
            
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };
            
            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    const store = db.createObjectStore(this.storeName, { keyPath: 'url' });
                    store.createIndex('timestamp', 'timestamp', { unique: false });
                    store.createIndex('sessionId', 'sessionId', { unique: false });
                }
            };
        });
    }
    
    async get(url: string): Promise<ArrayBuffer | null> {
        if (!CacheConfig.enabled || !CacheConfig.tileCachingEnabled) return null;
        
        try {
            await this.init();
            if (!this.db) return null;
            
            return new Promise((resolve) => {
                const transaction = this.db!.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const request = store.get(url);
                
                request.onsuccess = () => {
                    const result = request.result;
                    if (!result) {
                        resolve(null);
                        return;
                    }
                    
                    // Check if expired
                    const now = Date.now();
                    if (now - result.timestamp > this.TILE_CACHE_EXPIRY) {
                        // Delete expired entry
                        this.delete(url);
                        resolve(null);
                        return;
                    }
                    
                    resolve(result.data);
                };
                
                request.onerror = () => resolve(null);
            });
        } catch (error) {
            logger.warn('Tile cache get failed:', error);
            return null;
        }
    }
    
    async set(url: string, data: ArrayBuffer, sessionId?: string): Promise<void> {
        if (!CacheConfig.enabled || !CacheConfig.tileCachingEnabled) return;
        
        try {
            await this.init();
            if (!this.db) return;
            
            const cacheItem = {
                url,
                data,
                timestamp: Date.now(),
                sessionId: sessionId || '',
            };
            
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            store.put(cacheItem);
            
        } catch (error) {
            logger.warn('Tile cache set failed:', error);
        }
    }
    
    async delete(url: string): Promise<void> {
        try {
            await this.init();
            if (!this.db) return;
            
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            store.delete(url);
        } catch (error) {
            logger.warn('Tile cache delete failed:', error);
        }
    }
    
    async clearExpired(): Promise<void> {
        try {
            await this.init();
            if (!this.db) return;
            
            const now = Date.now();
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const index = store.index('timestamp');
            
            const request = index.openCursor();
            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest).result;
                if (cursor) {
                    if (now - cursor.value.timestamp > this.TILE_CACHE_EXPIRY) {
                        cursor.delete();
                    }
                    cursor.continue();
                }
            };
        } catch (error) {
            logger.warn('Tile cache cleanup failed:', error);
        }
    }
    
    async clearBySession(sessionId: string): Promise<void> {
        try {
            await this.init();
            if (!this.db) return;
            
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const index = store.index('sessionId');
            
            const request = index.openCursor(IDBKeyRange.only(sessionId));
            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest).result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                }
            };
        } catch (error) {
            logger.warn('Tile cache session cleanup failed:', error);
        }
    }
}

// Global tile cache instance
const tileCache = new TileCache();

// Periodic cleanup of expired tiles
setInterval(() => {
    tileCache.clearExpired();
}, 10 * 60 * 1000); // Clean up every 10 minutes

/**
 * Fetch and cache a 3D tile with proper session management
 * @param url The tile URL to fetch
 * @param sessionId Optional session ID for cache management
 * @returns Promise<ArrayBuffer | null>
 */
export const fetchTile = async (url: string, sessionId?: string): Promise<ArrayBuffer | null> => {
    // Check if tile caching is enabled
    if (!CacheConfig.enabled || !CacheConfig.tileCachingEnabled) {
        logger.debug('Tile caching is disabled, fetching directly from network');
        try {
            const response = await fetch(url, {
                method: 'GET',
                cache: 'default',
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            return await response.arrayBuffer();
        } catch (error) {
            logger.error(`Failed to fetch tile: ${url}`, error);
            return null;
        }
    }

    // Warn user about potential ToS issues
    if (process.env.NODE_ENV === 'development') {
        console.warn('🚨 3D Tile caching is enabled. Please ensure you have proper licensing from Google and review their Terms of Service.');
    }

    try {
        // Check cache first
        const cachedData = await tileCache.get(url);
        if (cachedData) {
            logger.debug(`Using cached tile: ${url.substring(0, 100)}...`);
            return cachedData;
        }
        
        // Fetch from network
        const response = await fetch(url, {
            method: 'GET',
            // Respect any cache headers from Google
            cache: 'default',
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.arrayBuffer();
        
        // Cache the data
        await tileCache.set(url, data, sessionId);
        
        logger.debug(`Cached new tile: ${url.substring(0, 100)}...`);
        return data;
        
    } catch (error) {
        logger.error(`Failed to fetch tile: ${url}`, error);
        return null;
    }
};

/**
 * Batch fetch multiple tiles with caching
 * @param urls Array of tile URLs to fetch
 * @param sessionId Optional session ID for cache management
 * @returns Promise<Map<string, ArrayBuffer | null>>
 */
export const fetchTiles = async (urls: string[], sessionId?: string): Promise<Map<string, ArrayBuffer | null>> => {
    const results = new Map<string, ArrayBuffer | null>();
    
    // Process tiles in parallel but limit concurrency to avoid overwhelming the server
    const BATCH_SIZE = 6;
    
    for (let i = 0; i < urls.length; i += BATCH_SIZE) {
        const batch = urls.slice(i, i + BATCH_SIZE);
        const promises = batch.map(url => 
            fetchTile(url, sessionId).then(data => ({ url, data }))
        );
        
        const batchResults = await Promise.all(promises);
        batchResults.forEach(({ url, data }) => {
            results.set(url, data);
        });
    }
    
    return results;
};

/**
 * Clear tiles associated with a specific session
 * @param sessionId The session ID to clear tiles for
 */
export const clearTilesBySession = async (sessionId: string): Promise<void> => {
    await tileCache.clearBySession(sessionId);
};

/**
 * Manual cleanup of expired tiles
 */
export const cleanupExpiredTiles = async (): Promise<void> => {
    await tileCache.clearExpired();
};


